import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeContext';
import { useBiblesQuery } from '../hooks/queries';
import { useDeleteBible } from '../hooks/mutations';
import BibleUploadModal from './BibleUploadModal';
import BookmarksMenu from './BookmarksMenu';

interface HeaderProps {
  onSearch: (query: string) => void;
  onClearSearch: () => void;
}

export default function Header({ onSearch, onClearSearch }: HeaderProps) {
  const { user, isAuthenticated, logout, isAdmin } = useUser();
  const { theme, setTheme } = useTheme();
  const { data: bibles = [] } = useBiblesQuery();
  const deleteBible = useDeleteBible();

  const [searchInput, setSearchInput] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteMenu, setShowDeleteMenu] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      onSearch(searchInput.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    onClearSearch();
  };

  const handleDeleteBible = async (bibleId: number, bibleName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${bibleName}"? This will delete all verses associated with this translation.`)) return;

    try {
      await deleteBible.mutateAsync(bibleId);
      setShowDeleteMenu(false);
    } catch (err) {
      console.error('Failed to delete bible:', (err as Error).message);
      alert(`Failed to delete: ${(err as Error).message}`);
    }
  };

  const themeButton = (value: 'light' | 'dark', label: string, path: React.ReactNode) => (
    <button
      onClick={() => setTheme(value)}
      title={label}
      aria-label={label}
      aria-pressed={theme === value}
      className={`p-1.5 rounded-full transition-colors ${
        theme === value
          ? 'bg-white text-gray-800 shadow-sm dark:bg-ink-600 dark:text-parchment-200'
          : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
      }`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        {path}
      </svg>
    </button>
  );

  return (
    <header className="flex-shrink-0 bg-white border-b border-gray-200 dark:bg-ink-800 dark:border-ink-600">
      <div className="px-6 py-3 flex items-center justify-between gap-6">
        {/* Title */}
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-parchment-100 leading-tight">
            Bible Explorer
          </h1>
          <p className="hidden lg:block text-sm text-gray-500 dark:text-gray-400 italic truncate">
            „Cuvântul Tău este o candelă pentru picioarele mele și o lumină pe cărarea mea.”
            <span className="not-italic"> — Psalmul 119:105</span>
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="relative hidden sm:block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
            </svg>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search"
              className="w-44 lg:w-64 pl-9 pr-8 py-2 rounded-full text-sm bg-gray-100 border border-gray-200 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-parchment-400 dark:bg-ink-700 dark:border-ink-600 dark:text-parchment-100 dark:placeholder-gray-500"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </form>

          <BookmarksMenu onNavigate={handleClearSearch} />

          {/* Light / dark toggle */}
          <div className="flex items-center gap-1 p-1 rounded-full bg-gray-100 border border-gray-200 dark:bg-ink-700 dark:border-ink-600">
            {themeButton(
              'light',
              'Light mode',
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            )}
            {themeButton(
              'dark',
              'Dark mode',
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            )}
          </div>

          {/* Upload / delete translations (admin only) */}
          {isAdmin() && (
            <>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200 transition-colors dark:bg-ink-700 dark:border-ink-600 dark:text-parchment-100 dark:hover:bg-ink-600"
                title="Upload Translation"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 15V4m0 0L8 8m4-4l4 4" />
                </svg>
                <span className="hidden md:inline">Upload</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowDeleteMenu(!showDeleteMenu)}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors dark:hover:bg-ink-700"
                  title="Delete Translation"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>

                {showDeleteMenu && (
                  <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg py-1 z-50 bg-white border border-gray-200 dark:bg-ink-700 dark:border-ink-600">
                    <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider border-b text-gray-500 border-gray-100 dark:text-gray-400 dark:border-ink-600">
                      Delete Translation
                    </div>
                    {bibles.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No translations found</div>
                    ) : (
                      bibles.map((bible) => (
                        <button
                          key={bible.id}
                          onClick={() => handleDeleteBible(bible.id, bible.name)}
                          disabled={deleteBible.isPending}
                          className="w-full text-left px-3 py-2 text-sm transition-colors disabled:opacity-50 text-gray-700 hover:bg-red-50 hover:text-red-600 dark:text-parchment-100 dark:hover:bg-ink-600 dark:hover:text-red-400"
                        >
                          {bible.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Account */}
          {isAuthenticated() ? (
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" />
              </svg>
              <div className="leading-tight text-right">
                <p className="text-sm text-gray-700 dark:text-parchment-100 truncate max-w-[12rem]">{user?.email}</p>
                <button
                  onClick={handleLogout}
                  className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                >
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <Link
              to="/login"
              className="px-3 py-2 text-sm font-medium rounded-lg text-blue-600 hover:bg-blue-50 transition-colors dark:text-blue-400 dark:hover:bg-ink-700"
            >
              Login
            </Link>
          )}
        </div>
      </div>

      {showUploadModal && (
        <BibleUploadModal onClose={() => setShowUploadModal(false)} />
      )}
    </header>
  );
}
